const DEFAULT_TIMEZONE = "Asia/Kolkata";

function normalizeTime(value) {
  if (!value) return "";
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return "";

  const hh = String(Math.min(Math.max(Number(match[1]), 0), 23)).padStart(2, "0");
  const mm = String(Math.min(Math.max(Number(match[2]), 0), 59)).padStart(2, "0");
  return `${hh}:${mm}`;
}

function timeToMinutes(value) {
  const normalized = normalizeTime(value);
  if (!normalized) return null;

  const [hh, mm] = normalized.split(":").map(Number);
  return hh * 60 + mm;
}

function getNowInTimezone(timezone = DEFAULT_TIMEZONE) {
  const tz = timezone || DEFAULT_TIMEZONE;

  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date());

    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const hour = Number(map.hour);
    const minute = Number(map.minute);

    if (Number.isFinite(hour) && Number.isFinite(minute)) {
      return {
        time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
        minutes: hour * 60 + minute,
        timezone: tz,
      };
    }
  } catch {
    // fallback below
  }

  const d = new Date();
  return {
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    minutes: d.getHours() * 60 + d.getMinutes(),
    timezone: DEFAULT_TIMEZONE,
  };
}

function isNowInsideWindow(nowMinutes, openingMinutes, closingMinutes) {
  if (openingMinutes === null || closingMinutes === null) return true;
  if (openingMinutes === closingMinutes) return true;

  if (openingMinutes < closingMinutes) {
    return nowMinutes >= openingMinutes && nowMinutes <= closingMinutes;
  }

  return nowMinutes >= openingMinutes || nowMinutes <= closingMinutes;
}

function labelForStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "permanently_closed") return "PERMANENTLY CLOSED";
  if (s === "inactive") return "INACTIVE";
  if (s === "closed_by_time" || s === "closed") return "CLOSED BY TIME";
  if (s === "open") return "OPEN NOW";
  return "UNKNOWN";
}

export function getWarehouseRuntimeState(warehouse = {}) {
  const manualStatus = String(
    warehouse?.manual_status ?? warehouse?.manualStatus ?? warehouse?.status ?? ""
  )
    .trim()
    .toLowerCase();

  const openingTime = warehouse?.opening_time || warehouse?.openingTime;
  const closingTime = warehouse?.closing_time || warehouse?.closingTime;
  const hasTimeWindow = Boolean(openingTime || closingTime);

  // Prefer browser-side calculation when timing data exists so the UI flips
  // without waiting for a backend refresh.
  if (hasTimeWindow) {
    const timezone = warehouse?.timezone || DEFAULT_TIMEZONE;
    const now = getNowInTimezone(timezone);
    const openingMinutes = timeToMinutes(openingTime);
    const closingMinutes = timeToMinutes(closingTime);

    const isPermanentlyClosed = manualStatus === "permanently_closed";
    const isManualActive = manualStatus === "active";
    const isOpenByTime = !isPermanentlyClosed && isNowInsideWindow(now.minutes, openingMinutes, closingMinutes);
    const operationalStatus = isPermanentlyClosed
      ? "permanently_closed"
      : !isManualActive
        ? "inactive"
        : isOpenByTime
          ? "open"
          : "closed_by_time";

    return {
      manualStatus,
      isManualActive,
      operationalStatus,
      isOpenNow: isOpenByTime,
      isServiceableNow: isManualActive && isOpenByTime && !isPermanentlyClosed,
      label: labelForStatus(operationalStatus),
      reason: "",
    };
  }

  const backendOperational = String(
    warehouse?.operational_status ?? warehouse?.operationalStatus ?? ""
  )
    .trim()
    .toLowerCase();

  const backendServiceable =
    warehouse?.is_serviceable_now ?? warehouse?.isServiceableNow ?? null;

  if (backendOperational) {
    const isManualActive = manualStatus === "active";
    const isServiceableNow = Boolean(backendServiceable);

    return {
      manualStatus,
      isManualActive,
      operationalStatus: backendOperational,
      isOpenNow: backendOperational === "open",
      isServiceableNow,
      label: labelForStatus(backendOperational),
      reason: warehouse?.serviceability_reason || warehouse?.serviceabilityReason || "",
    };
  }

  const isPermanentlyClosed = manualStatus === "permanently_closed";

  return {
    manualStatus,
    isManualActive: manualStatus === "active",
    operationalStatus: isPermanentlyClosed ? "permanently_closed" : manualStatus === "active" ? "open" : "inactive",
    isOpenNow: manualStatus === "active" && !isPermanentlyClosed,
    isServiceableNow: manualStatus === "active" && !isPermanentlyClosed,
    label: manualStatus === "active" ? "OPEN NOW" : isPermanentlyClosed ? "PERMANENTLY CLOSED" : manualStatus ? "INACTIVE" : "UNKNOWN",
    reason: "",
  };
}
